import { def } from "@jamesgmarks/utilities";
import { Checkbox, FormControl, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Button, Col, Container, Form, InputGroup, Row,
} from "react-bootstrap";
import Tab from "react-bootstrap/esm/Tab";
import Tabs from "react-bootstrap/esm/Tabs";
import { loadSalesReps, markCommissionsAsPaidByFile } from "../../../redux/features/commissions/actions";
import { useAppSelector } from "../../../redux/hooks";
import { CommissionsPayable } from "./CommissionsPayable";
import { CommissionsUnpaid } from "./CommissionsUnpaid";
import { CommissionsMonthly } from "./CommissionsMonthly";
import { dispatch } from "../../../redux/store";
import { setCommissionSource } from "../../../redux/features/commissions/commissionsSlice";
import { CommissionsPaid } from "./CommissionsPaid";
import { useAuth } from "../../../customHooks/useAuth";

/**
 * Top-level container for the Commissions section.
 *
 * Renders the sales rep selector, the source picker, and the upload control, then
 * passes the selected rep IDs down to all commission tab components
 * ({@link CommissionsPayable}, {@link CommissionsPaid}, {@link CommissionsMonthly},
 * {@link CommissionsUnpaid}) so that every tab always reflects the same rep selection.
 *
 * **Permission-gated rep selector behaviour:**
 * - Users with `READ_ALL_COMMISSIONS` (or `FULL_ACCESS`) see a MUI multi-select that allows
 *   any combination of sales reps. Starts empty ("Select reps…"); no fetch fires until at
 *   least one rep is chosen.
 * - Users with only `READ_OWN_COMMISSIONS` see a disabled read-only display of their own
 *   name. Their rep ID is resolved once the sales rep list has loaded and locked into state.
 */
export const CommissionsTabView = () => {
  const salesReps = useAppSelector(state => state.commissions.salesRepList);
  const commissionSource = useAppSelector(state => state.commissions.commissionSource);
  const { userId, permissions } = useAuth();
  // TODO: Are these useState states redundant? (use hasPermission() ?)
  const [
    readAllCommissionsPermission,
  ] = useState((permissions.FULL_ACCESS === 'grant' || permissions.READ_ALL_COMMISSIONS === 'grant'));
  const [
    readOwnCommissionsPermission,
  ] = useState((permissions.FULL_ACCESS === 'grant' || permissions.READ_OWN_COMMISSIONS === 'grant'));
  const [
    file,
    setFile,
  ] = useState({} as File);

  if (!def(salesReps) || (salesReps.length === 0)) {
    loadSalesReps();
  }

  const [
    updateCommissionPaidStatusPermission,
  ] = useState((permissions.FULL_ACCESS === 'grant' || permissions.UPDATE_COMMISSION_PAID_STATUS === 'grant'));

  // Multi-rep selection state. Admins start with nothing selected (show all prompt);
  // own-rep users are locked to their own ID once the salesRep list loads.
  const [salesRepIds, setSalesRepIds] = useState<number[]>([]);

  useEffect(() => {
    if (!readAllCommissionsPermission && readOwnCommissionsPermission) {
      const ownRepId = salesReps.find((s) => s.user.id === userId)?.id ?? 0;
      if (ownRepId > 0) {
        setSalesRepIds([ownRepId]);
      }
    }
  }, [salesReps, readAllCommissionsPermission, readOwnCommissionsPermission, userId]);

  const ownRep = salesReps.find((s) => s.user.id === userId);

  /**
   * Handles change events from the MUI multi-select. MUI can emit a comma-separated
   * string when the value is programmatically reset; in that case the selection is
   * cleared to `[]`. Otherwise the event value is cast to `number[]` and stored.
   */
  const handleRepSelectionChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSalesRepIds(typeof value === 'string' ? [] : (value as number[]));
  };

  return (
    <Container fluid>
      <Row>
        {/* Admins / managers: multi-select for any combination of sales reps */}
        {readAllCommissionsPermission && (
          <Col xs={4}>
            <Form.Group>
              <Form.Label>Sales Reps</Form.Label>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  displayEmpty
                  value={salesRepIds}
                  onChange={handleRepSelectionChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) =>
                    (selected as number[]).length === 0
                      ? <em style={{ color: '#aaa' }}>Select reps…</em>
                      : salesReps
                        .filter(sr => (selected as number[]).includes(sr.id))
                        .map(sr => `${sr.user.firstName} ${sr.user.lastName}`)
                        .join(', ')
                  }
                >
                  {salesReps.map((sr) => (
                    <MenuItem key={sr.id} value={sr.id}>
                      <Checkbox checked={salesRepIds.includes(sr.id)} />
                      <ListItemText primary={`${sr.user.firstName} ${sr.user.lastName}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Form.Group>
          </Col>
        )}
        {/* Sales reps: locked read-only display of their own name */}
        {!readAllCommissionsPermission && readOwnCommissionsPermission && (
          <Col xs={4}>
            <Form.Group>
              <Form.Label>Sales Rep</Form.Label>
              <Form.Control
                value={ownRep?.id ?? 0}
                disabled
                as="select"
              >
                <option value={ownRep?.id ?? 0}>
                  {ownRep
                    ? `${ownRep.user.firstName} ${ownRep.user.lastName}`
                    : 'Loading…'}
                </option>
              </Form.Control>
            </Form.Group>
          </Col>
        )}
        <Col xs={4}>
          <Form.Group>
            <Form.Label>Source</Form.Label>
            <Form.Control
              value={commissionSource}
              onChange={(e) => dispatch(setCommissionSource((e.target.value as 'legacy' | 'hydra' | 'both')))}
              as="select"
            >
              <option key={'hydra'} value={'hydra'}>Hydra</option>
              <option key={'legacy'} value={'legacy'}>Legacy</option>
              <option key={'both'} value={'both'}>Both</option>
            </Form.Control>
          </Form.Group>
        </Col>
        {updateCommissionPaidStatusPermission && (
          <Col xs={4}>
            <Form.Group>
              <Form.Label>Upload Paid Commissions</Form.Label>
              <InputGroup className="mb-3">
                <InputGroup.Append>
                  <Form.File
                    id="custom-file"
                    label={file.name ? file.name : 'No File Selected'}
                    accept='.csv'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const numFiles = e?.target?.files?.length || 0;
                      const choosenFile = (numFiles > 0 && e.target?.files !== null) ? e.target?.files[0] : null;
                      if (choosenFile) {
                        setFile(choosenFile);
                      }
                    }}
                    custom
                  />
                  <Button
                    disabled={!file.name}
                    variant="primary"
                    onClick={() => file !== null ? markCommissionsAsPaidByFile(file) : null}
                  >Upload</Button>
                </InputGroup.Append>
                <InputGroup.Append />
              </InputGroup>
            </Form.Group>
          </Col>
        )}
      </Row>

      {(readOwnCommissionsPermission || readAllCommissionsPermission)
        && <>
          {readAllCommissionsPermission && salesRepIds.length === 0 && (
            <Row className="mt-4">
              <Col>
                <p className="text-muted text-center">
                  Select one or more sales reps above to load commission data.
                </p>
              </Col>
            </Row>
          )}
          {salesRepIds.length > 0 && (
            <Tabs
              defaultActiveKey="payable"
              id="uncontrolled-tab-example"
              transition={false}
              mountOnEnter={true}
              unmountOnExit={true}
            >
              <Tab eventKey="payable" title="Commissions Payable">
                <CommissionsPayable salesRepIds={salesRepIds} />
              </Tab>
              <Tab eventKey="paid" title="Commissions Paid">
                <CommissionsPaid salesRepIds={salesRepIds} />
              </Tab>
              <Tab eventKey="monthly" title="Commissions Monthly">
                <CommissionsMonthly salesRepIds={salesRepIds} />
              </Tab>
              <Tab eventKey="unpaid" title="Unpaid Commissions">
                <CommissionsUnpaid salesRepIds={salesRepIds} />
              </Tab>
            </Tabs>
          )}
        </>
      }
    </Container>
  );
};
